import React, { useEffect, useState } from "react";
import OrganizationDataTable from "../../../../components/organizationData";
import OrganizationTableForm from "../../../../components/organizationTableForm";
import ModalManager from "../../../../components/modalManager";
import styles from "./organization.module.css";
import apiCall from "../../../API/apiService";
import apiEndpoints from "../../../API/apiEndpoints";
import toast from "react-hot-toast";
import { Pagination } from 'antd';
import Typography from "@mui/material/Typography";
import { useDispatch } from "react-redux";
import { setOpenModal } from "../../../../redux/Actions/UserAction";
import { createOrganisation } from "../../../assets/svgComponent";

const Organization = () => {
	const [rows, setRows] = useState([]);
	const [page, setPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(15);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [newItem, setNewItem] = useState({
		id: "",
		name: "",
		industry_type: "",
		cities: [{ name: "", state: "", country: "" }],
		no_employees: "",
		poc_contact: "",
		poc_email: "",
		poc_name: "",
		color: { primary: "" },
		logo: ""
	});
	const [editingId, setEditingId] = useState(null);
	const [editMode, setEditMode] = useState(false);
	const dispatch = useDispatch();

	useEffect(() => {
		fetchData();
	}, [page, rowsPerPage]);

	const toasty = (message, type) => {
		if (type === "error") {
			toast.error(message, {
				style: {
					border: "1px solid #2c5da7",
					padding: "16px",
					color: "#2c5da7",
				},
				iconTheme: {
					primary: "#2c5da7",
					secondary: "#FFFAEE",
				},
			});
		} else {
			toast.success(message, {
				style: {
					border: "1px solid #2c5da7",
					padding: "16px",
					color: "#2c5da7",
				},
				iconTheme: {
					primary: "#2c5da7",
					secondary: "#FFFAEE",
				},
			});
		}
	};

	const fetchData = async () => {
		setIsLoading(true);
		try {
			const response = await apiCall("get", `${apiEndpoints.ORGANIZATION}?page=${page}&page_size=${rowsPerPage}`);

			if (response && Array.isArray(response.items)) {
				setRows(response.items);
				setTotalItems(response.total);
				setTotalPages(Math.ceil(response.total / rowsPerPage));
			} else {
				console.error("Unexpected response structure:", response);
				setRows([]);
			}
		} catch (error) {
			console.error("Error fetching organization data:", error);
			toasty(`Error fetching organization data: ${error.message}`, "error");
			setRows([]);
		} finally {
			setIsLoading(false);
		}
	};

	const putData = async (obj) => {
		try {
			// Ensure that an ID is provided for the update
			if (!obj.id) {
				throw new Error('Organization ID is required for updates');
			}
	
			const formData = new FormData();
	
			// Structure the organization data, providing defaults for empty fields
			const organisationData = {
				id: obj.id,
				name: obj.name || '', // Default to empty string if not provided
				industry_type: obj.industry_type || '', // Default to empty string if not provided
				cities: obj.cities || [], // Default to empty array if not provided
				no_employees: obj.no_employees === "" || obj.no_employees == null ? 0 : obj.no_employees, // Ensure default value for no_employees
				poc_name: obj.poc_name || '', // Default to empty string if not provided
				poc_email: obj.poc_email || '', // Default to empty string if not provided
				poc_contact: obj.poc_contact || '', // Default to empty string if not provided
				color: obj.color?.primary ? obj.color : { primary: "#FAFAFA" }, // Provide default color if not specified
			};
	
			// Append the organization data to the formData as JSON
			formData.append('organisation_data', JSON.stringify(organisationData));
	
			// Handle the logo, either using the provided logo or setting a default one
			const logoFile = obj.logo instanceof File ? obj.logo : new File([], 'Frame 5.png', { type: 'image/png' });
			formData.append('logo', logoFile);
	
			// Make the PUT request to the API
			const data = await apiCall("put", apiEndpoints.ORGANIZATION, formData);
	
			// Handle the response, if an organization is updated
			if (data) {
				// Update the rows in the state (assuming setRows is a function for state management)
				setRows(prevRows => prevRows.map(item =>
					item.id === data.id ? data : item // Replace the updated organization in the list
				));
	
				// Show a success message
				toasty("Organization Updated successfully");
			}
		} catch (error) {
			// Handle error during the update process
			console.error("Error updating organization:", error);
	
			// Extract and display a meaningful error message
			const errorMessage = error.response?.data?.detail?.[0]?.msg ||
				error.message ||
				"An unknown error occurred";
			toasty(`Error updating organization: ${errorMessage}`, "error");
	
			// Re-throw the error to propagate it, if necessary
			throw error;
		}
	};
	

	const deleteData = async (id) => {
		try {
			if (!id) {
				throw new Error("Organization ID is required for deletion");
			}
			await apiCall("delete", `${apiEndpoints.ORGANIZATION}/${id}`);
			setRows(prevRows => prevRows.filter(item => item.id !== id));
			toasty("Organization Deleted successfully");
		} catch (error) {
			console.error("Error deleting organization:", error);
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;

		if (name === "cities") {
			setNewItem((prevItem) => ({
				...prevItem,
				cities: [{ ...prevItem.cities[0], ...value[0] }],
			}));
		} else if (name === "color.primary") {
			setNewItem((prevItem) => ({
				...prevItem,
				color: {
					primary: value
				}
			}));
		} else {
			setNewItem((prevItem) => ({
				...prevItem,
				[name]: name === "no_employees" ? (value === "" ? "" : Number(value)) : value,
			}));
		}
	};

	const postData = async (obj) => {
		try {
			// Ensure obj has the expected properties before proceeding
			if (!obj || !obj.name || !obj.industry_type || !obj.cities) {
				throw new Error("Required fields are missing in the object.");
			}
	
			const formData = new FormData();
	
			// Create a structured data object
			const organisationData = {
				name: obj.name,
				industry_type: obj.industry_type,
				cities: obj.cities,
				no_employees: obj.no_employees || 0, // Default to 0 if no_employees is falsy
				poc_name: obj.poc_name,
				poc_email: obj.poc_email,
				poc_contact: obj.poc_contact,
				color: (obj.color && obj.color.primary) ?
					obj.color : { primary: "#FAFAFA" }, // Ensure default color if not provided
			};
	
			// Append organization data to formData as JSON
			formData.append('organisation_data', JSON.stringify(organisationData));
	
			// Handle the logo, ensure it's a File or use default image
			const logoFile = obj.logo instanceof File ? obj.logo : new File([], 'Frame 5.png', { type: 'image/png' });
			formData.append('logo', logoFile);
	
			// Make API call
			const data = await apiCall("post", apiEndpoints.ORGANIZATION, formData);
	
			// Update rows with the response data
			setRows(prevRows => [...prevRows, data]);
	
			// Show success message
			toasty("Organization Created successfully");
		} catch (error) {
			// Handle error and show appropriate message
			console.error("Error creating organization:", error);
			const errorMessage = error.response?.data?.detail?.[0]?.msg ||
				error.message ||
				"An unknown error occurred";
			toasty(`Error creating organization: ${errorMessage}`, "error");
		}
	};
	
	const handleAddRow = async () => {
		try {
			// Ensure newItem has the necessary fields
			if (!newItem.name || !newItem.industry_type || !newItem.cities) {
				toasty("Please fill in all required fields.", "error");
				return;
			}
	
			// Create raw object for posting
			const raw = {
				name: newItem.name,
				industry_type: newItem.industry_type,
				cities: newItem.cities,
				no_employees: newItem.no_employees === "" ? 0 : Number(newItem.no_employees),
				poc_contact: newItem.poc_contact,
				poc_email: newItem.poc_email,
				poc_name: newItem.poc_name,
				color: newItem.color,
				logo: newItem.logo,
			};
	
			// Post data
			await postData(raw);
	
			// Refresh data
			await fetchData();
	
			// Reset form
			resetForm();
		} catch (error) {
			console.error("Error adding row:", error);
			toasty("An error occurred while adding the organization.", "error");
		}
	};
	

	const handleSaveRow = async () => {
		try {
			if (!newItem.id) {
				throw new Error('Organization ID is required for updates');
			}

			const raw = {
				id: newItem.id,
				name: newItem.name || null,
				industry_type: newItem.industry_type || null,
				cities: newItem.cities || null,
				no_employees: newItem.no_employees === "" ? null : Number(newItem.no_employees),
				poc_name: newItem.poc_name || null,
				poc_email: newItem.poc_email || null,
				poc_contact: newItem.poc_contact || null,
				color: { primary: newItem.color.primary },
				logo: newItem.logo,
			};

			await putData(raw);
			resetForm();
		} catch (error) {
			console.error("Failed to save row:", error);
			toasty(`Failed to save: ${error.message}`, "error");
		}
	};

	const handleEditRow = (id) => {
		setEditingId(id);
		const itemToEdit = rows.find((row) => row.id === id);
		setNewItem({
			...itemToEdit,
			no_employees: itemToEdit.no_employees !== null
				? itemToEdit.no_employees.toString()
				: "",
		});
		dispatch(setOpenModal({ modalVisible: true }));
	};

	const resetForm = () => {
		setNewItem({
			id: "",
			name: "",
			industry_type: "",
			cities: [{ name: "", state: "", country: "" }],
			no_employees: "",
			poc_contact: "",
			poc_email: "",
			poc_name: "",
			color: { primary: "" },
			logo: null
		});
		setEditingId(null);
	};

	const handleDeleteRow = async (id) => {
		await deleteData(id);
		await fetchData();
	};

	const handleOpenCreateModal = () => {
		resetForm();
		dispatch(setOpenModal({ modalVisible: true }));
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
	};

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		if (file && file.type === 'image/png') {
			setNewItem(prevItem => ({
				...prevItem,
				logo: file
			}));
		} else {
			toasty('Please select a PNG file', 'error');
		}
	};

	return (
		<div className={styles.container}>
			<div className={styles.buttonContainer}>
				<span>List of organisations</span>
				<img style={{cursor:'pointer'}} onClick={handleOpenCreateModal} src={createOrganisation}/>
			</div>

			<ModalManager customWidth={window.innerWidth/300} >
				<OrganizationTableForm
					item={newItem}
					onChange={handleInputChange}
					onFileChange={handleFileChange}
					onSave={editingId !== null ? handleSaveRow : handleAddRow}
					onClear={resetForm}
					editMode={editingId !== null}
					setEditMode={setEditMode}
				/>
			</ModalManager>
				<OrganizationDataTable
					rows={rows}
					handleEditRow={handleEditRow}
					handleDeleteRow={handleDeleteRow}
					page={page}
					totalItems={totalItems}
					onPageChange={handlePageChange}
				/>
			
		</div>
	);
};

export default Organization;
